import { BpkCalendarNav } from '@skyscanner-internal/backpack-web/bpk-component-calendar';
import {
  BpkBox,
  BpkSpacing,
  BpkStack,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText from '@skyscanner-internal/backpack-web/bpk-component-text';
import { useDispatch, useSelector } from 'react-redux';

import { useLocalisation } from '../../../../services/contextAdaptor/LocalisationContext';
import { clearTripDuration } from '../../actions/trip-duration-actions';
import * as constants from '../../helpers/constants/dataTypeConstants';
import { useDateSelection } from '../../helpers/hooks/useDateSelection';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { trackInteractionEvent } from '../../helpers/logging/trackInteractionEvent';
import {
  getMaxDate,
  getMinDate,
  getSelectedMonth,
  isUserDateSelectionAllowed,
} from '../../selectors';

import type { Direction, State } from '../../selectors/types';

type MonthSelectorProps = {
  direction: Direction;
  id: string;
};

const MonthSelector = ({ direction, id }: MonthSelectorProps) => {
  const dispatch = useDispatch();
  const translate = useTranslation();
  const { selectMonth } = useDateSelection();
  const { formatMonth } = useLocalisation();

  const allowed = useSelector(isUserDateSelectionAllowed);
  const month = useSelector((state: State) =>
    getSelectedMonth(state, direction),
  );
  const minDate = useSelector(getMinDate);
  const maxDate = useSelector(getMaxDate);

  const handleMonthChange = (
    _event: unknown,
    data: { month: Date; source: string },
  ) => {
    const date = data.month;
    dispatch(clearTripDuration());
    selectMonth(
      date.getMonth() + 1,
      date.getFullYear(),
      direction,
      data.source,
    );
    trackInteractionEvent({ name: 'Calendar_Month_Changed' });
  };

  if (!month) {
    return null;
  }

  const monthFormatter = allowed ? formatMonth : () => '';

  const label = translate(
    direction === constants.OUTBOUND_DIRECTION
      ? 'outbound_month_label'
      : 'inbound_month_label',
  );

  return (
    <BpkBox data-testid="month-selector">
      <BpkStack
        direction="row"
        align="center"
        gap={BpkSpacing.SM}
        marginBlock={{ base: BpkSpacing.Base, 'small-tablet': BpkSpacing.SM }}
        paddingStart={BpkSpacing.MD}
      >
        <BpkBox flexShrink={0}>
          <BpkText tagName="span">{label}</BpkText>
        </BpkBox>
        <BpkBox>
          <BpkCalendarNav
            id={id}
            formatMonth={monthFormatter}
            disabled={!allowed}
            month={month}
            minDate={minDate}
            maxDate={maxDate}
            onMonthChange={handleMonthChange}
            previousMonthLabel={translate('previous_month_label')}
            nextMonthLabel={translate('next_month_label')}
            changeMonthLabel={translate('change_month_label')}
          />
        </BpkBox>
      </BpkStack>
    </BpkBox>
  );
};

export const getComponentWithDirection =
  (direction: Direction) => (props: { id: string }) => (
    <MonthSelector direction={direction} {...props} />
  );

export default MonthSelector;
